import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAgentContext } from '../../hooks/useAgentContext';
import { ChevronRightIcon } from '../ui';
import type {
  BusinessCaseDetails,
  BusinessCaseSummary,
} from '../../services/agent/AgentService';

interface BreadcrumbItem {
  label: string;
  path?: string;
  isCurrentPage?: boolean;
}

// Path-to-label mapping for static routes
const STATIC_ROUTE_LABELS: Record<string, string> = {
  '': 'Home',
  admin: 'Admin',
  profile: 'Profile',
  main: 'Home',
  projects: 'Projects',
  new: 'New',
};

// Helper function to get case title - NOT a hook
const getCaseTitle = (
  caseId: string | undefined,
  currentCaseDetails: BusinessCaseDetails | null,
  cases: BusinessCaseSummary[]
): string | null => {
  if (!caseId) return null;

  // First try to get from current case details if it matches
  if (currentCaseDetails?.case_id === caseId) {
    return currentCaseDetails.title || null;
  }

  // Fallback to cases list
  const caseFromList = cases.find((c) => c.case_id === caseId);
  return caseFromList?.title || null;
};

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const { currentCaseDetails, cases } = useAgentContext();

  // Split pathname into segments and filter out empty strings
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Get case title for current route if applicable
  const currentCaseId = React.useMemo(() => {
    const caseMatch = location.pathname.match(/\/cases\/([^/]+)/);
    return caseMatch ? caseMatch[1] : undefined;
  }, [location.pathname]);

  const caseTitle = getCaseTitle(currentCaseId, currentCaseDetails, cases);

  // Generate breadcrumb items
  const breadcrumbItems = React.useMemo((): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    // Always start with Home for authenticated pages
    items.push({
      label: 'Home',
      path: '/main',
    });

    // Build breadcrumbs for each path segment
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const isLast = i === pathSegments.length - 1;
      const pathUpToSegment = '/' + pathSegments.slice(0, i + 1).join('/');

      // Handle special cases for dynamic routes
      if (segment === 'projects' && pathSegments[i + 1]) {
        // For /projects/:id or /projects/new routes
        const nextSegment = pathSegments[i + 1];

        // Add "Projects" if not already there
        if (!items.some((item) => item.label === 'Projects')) {
          items.push({
            label: 'Projects',
            path: '/main',
          });
        }

        if (nextSegment === 'new') {
          // For /projects/new
          items.push({
            label: 'New',
            isCurrentPage: true,
          });
        } else {
          // For /projects/:id - it's a project ID
          const projectId = nextSegment;
          const label = `Project ${projectId.substring(0, 8)}...`;

          items.push({
            label: label,
            isCurrentPage: i === pathSegments.length - 2,
            path:
              i === pathSegments.length - 2
                ? undefined
                : `/projects/${projectId}`,
          });
        }
        // Skip the next segment in iteration
        i++;
      } else if (segment === 'admin' && pathSegments[i + 1]) {
        // For /admin/:adminAction routes
        items.push({
          label: 'Admin',
          path: '/admin',
        });

        const adminAction = pathSegments[i + 1];
        const actionLabel = adminAction
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        items.push({
          label: actionLabel,
          isCurrentPage: true,
        });
        break;
      } else {
        // Handle standard static routes
        const label =
          STATIC_ROUTE_LABELS[segment] ||
          segment.charAt(0).toUpperCase() + segment.slice(1);

        // Skip adding Home if it's already there and we're on main route
        if (segment === 'main' && items.some((item) => item.label === 'Home')) {
          continue;
        }

        items.push({
          label: label,
          path: isLast ? undefined : pathUpToSegment,
          isCurrentPage: isLast,
        });
      }
    }

    return items;
  }, [pathSegments]);

  // Update breadcrumb items with case title if available
  const finalBreadcrumbItems = React.useMemo(() => {
    if (!caseTitle || !currentCaseId) return breadcrumbItems;

    return breadcrumbItems.map((item) => {
      if (item.label.startsWith('Case ') && item.label.includes('...')) {
        return { ...item, label: caseTitle };
      }
      return item;
    });
  }, [breadcrumbItems, caseTitle, currentCaseId]);

  // Don't show breadcrumbs on login, signup, or home pages
  if (['/login', '/signup', '/', '/main'].includes(location.pathname)) {
    return null;
  }

  // Don't show if no meaningful breadcrumbs
  if (finalBreadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav className="mb-4" aria-label="breadcrumb">
      <ol className="flex items-center gap-1 text-sm">
        {finalBreadcrumbItems.map((item, index) => {
          // const isLast = index === finalBreadcrumbItems.length - 1;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRightIcon className="w-4 h-4 mx-1 text-secondary-400" />
              )}
              {item.isCurrentPage || !item.path ? (
                <span className="text-secondary-900 font-medium">
                  {item.label}
                </span>
              ) : (
                <RouterLink
                  to={item.path}
                  className="text-secondary-600 hover:text-primary-600 hover:underline transition-colors"
                >
                  {item.label}
                </RouterLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
